import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Table, Alert, Spinner } from 'react-bootstrap';
import { Trash2, Plus } from 'lucide-react';
import { toast } from 'react-toastify';
import { inventoryAPI } from '../../services/inventory';

const EMPTY_ROW = { raw_material_id: '', quantity: '', unit_note: '' };

export default function BtpRecipeModal({ material, onClose }) {
  const [rows, setRows] = useState([{ ...EMPTY_ROW }]);
  const [initialIds, setInitialIds] = useState([]);
  const [yieldQty, setYieldQty] = useState('');
  const [rawMaterials, setRawMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [mats, recipe] = await Promise.all([
        inventoryAPI.getMaterials({ type: 'RAW', is_active: true }),
        inventoryAPI.getBtpRecipe(material.id),
      ]);
      setRawMaterials(mats || []);
      if (recipe) {
        setYieldQty(recipe.yield_quantity != null ? String(recipe.yield_quantity) : '');
        if (recipe.items && recipe.items.length > 0) {
          const mapped = recipe.items.map((r) => ({
            id: r.id,
            raw_material_id: r.raw_material_id,
            quantity: String(r.quantity),
            unit_note: r.unit_note || '',
          }));
          setRows(mapped);
          setInitialIds(mapped.map((r) => r.id).filter(Boolean));
        }
      }
    } catch {
      toast.error('Lỗi khi tải dữ liệu');
    } finally {
      setLoading(false);
    }
  }, [material.id]);

  useEffect(() => { loadData(); }, [loadData]);

  const updateRow = (idx, field, value) => {
    setRows((prev) => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const addRow = () => setRows((prev) => [...prev, { ...EMPTY_ROW }]);

  const removeRow = (idx) => setRows((prev) => prev.filter((_, i) => i !== idx));

  const handleSave = async () => {
    const items = rows.filter((r) => r.raw_material_id && Number(r.quantity) > 0);
    if (items.length === 0) {
      toast.warn('Thêm ít nhất 1 nguyên liệu');
      return;
    }
    setSaving(true);
    try {
      const parsedYield = yieldQty ? Number(yieldQty) : null;
      await inventoryAPI.upsertBtpRecipe(
        material.id,
        items.map((r) => ({
          raw_material_id: r.raw_material_id,
          quantity: Number(r.quantity),
          unit_note: r.unit_note || undefined,
        })),
        parsedYield,
      );
      // Delete rows that were removed
      const keptIds = new Set(rows.map((r) => r.id).filter(Boolean));
      for (const id of initialIds.filter((id) => !keptIds.has(id))) {
        await inventoryAPI.deleteBtpIngredient(material.id, id);
      }
      toast.success('Đã lưu công thức BTP');
      onClose(true);
    } catch (err) {
      toast.error(err?.message || 'Lỗi khi lưu');
    } finally {
      setSaving(false);
    }
  };

  const matMap = Object.fromEntries(rawMaterials.map((m) => [m.id, m]));
  const displayYield = yieldQty || 1;

  return (
    <Modal show onHide={() => onClose(false)} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Công thức BTP: {material.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-4"><Spinner animation="border" /></div>
        ) : (
          <>
            <Form.Group className="mb-3">
              <Form.Label>
                Sản lượng 1 mẻ ({material.unit})
              </Form.Label>
              <Form.Control
                type="number"
                min="0.001"
                step="any"
                value={yieldQty}
                onChange={(e) => setYieldQty(e.target.value)}
                placeholder={`VD: 300 (1 mẻ tạo 300 ${material.unit})`}
                style={{ maxWidth: 240 }}
              />
              <Form.Text className="text-muted">
                Để trống nếu nhập lượng NVL trực tiếp cho 1 {material.unit} BTP.
              </Form.Text>
            </Form.Group>

            <Alert variant="info" className="py-2">
              Nhập lượng NVL thô cho <strong>1 mẻ = {displayYield} {material.unit}</strong> bán thành phẩm.
            </Alert>

            <Table bordered size="sm">
              <thead>
                <tr>
                  <th>Nguyên liệu thô</th>
                  <th style={{ width: 120 }}>Số lượng</th>
                  <th style={{ width: 90 }}>Đơn vị</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={idx}>
                    <td>
                      <Form.Select
                        size="sm"
                        value={row.raw_material_id}
                        onChange={(e) => updateRow(idx, 'raw_material_id', e.target.value)}
                      >
                        <option value="">-- Chọn NVL --</option>
                        {rawMaterials.map((m) => (
                          <option key={m.id} value={m.id}>{m.name} ({m.unit})</option>
                        ))}
                      </Form.Select>
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        type="number"
                        min="0.001"
                        step="0.001"
                        value={row.quantity}
                        onChange={(e) => updateRow(idx, 'quantity', e.target.value)}
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        placeholder={row.raw_material_id ? matMap[row.raw_material_id]?.unit || '' : ''}
                        value={row.unit_note}
                        onChange={(e) => updateRow(idx, 'unit_note', e.target.value)}
                      />
                    </td>
                    <td className="text-center">
                      <Button variant="link" size="sm" className="text-danger p-0" onClick={() => removeRow(idx)}>
                        <Trash2 size={14} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Button variant="outline-secondary" size="sm" onClick={addRow}>
              <Plus size={14} className="me-1" />Thêm nguyên liệu
            </Button>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => onClose(false)}>Hủy</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving || loading}>
          {saving ? <Spinner size="sm" className="me-1" /> : null}
          Lưu công thức
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
